import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "@supabase/supabase-js";
import { demoUser } from "./data/demo";
import { getAdminStatus } from "./lib/admin";
import { isSupabaseConfigured, supabase } from "./lib/supabase";
import type { Profile } from "./types";

interface AuthContextValue {
  user: Profile | null;
  loading: boolean;
  isDemo: boolean;
  isAdmin: boolean;
  recoveryMode: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
  ) => Promise<"signed-in" | "confirmation-required">;
  signOut: () => Promise<void>;
  sendPasswordReset: (email: string) => Promise<void>;
  updatePassword: (password: string) => Promise<void>;
  exitRecovery: () => void;
  updateProfile: (displayName: string, avatarUrl?: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);
const demoEnabled = import.meta.env.DEV && import.meta.env.VITE_ENABLE_DEMO === "true";

async function profileFromUser(user: User): Promise<Profile> {
  const { data } = await supabase!.from("profiles").select("display_name, avatar_url").eq("id", user.id).maybeSingle();
  return {
    id: user.id,
    email: user.email ?? "",
    displayName: data?.display_name ?? user.user_metadata.display_name ?? user.email?.split("@")[0] ?? "Membre",
    avatarUrl: data?.avatar_url ?? undefined,
  };
}

async function adminStatusFromSession() {
  try {
    return await getAdminStatus();
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<Profile | null>(demoEnabled ? demoUser : null);
  const [isAdmin, setIsAdmin] = useState(demoEnabled);
  const [loading, setLoading] = useState(isSupabaseConfigured && !demoEnabled);
  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    if (demoEnabled || !supabase) {
      return;
    }
    void supabase.auth.getUser().then(async ({ data }) => {
      try {
        setUser(data.user ? await profileFromUser(data.user) : null);
        setIsAdmin(data.user ? await adminStatusFromSession() : false);
      } finally {
        setLoading(false);
      }
    });
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      void (async () => {
        setUser(session?.user ? await profileFromUser(session.user) : null);
        setIsAdmin(session?.user ? await adminStatusFromSession() : false);
        if (event === "PASSWORD_RECOVERY") setRecoveryMode(true);
      })();
    });
    return () => data.subscription.unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isDemo: demoEnabled,
      isAdmin,
      recoveryMode,
      async signIn(email, password) {
        if (demoEnabled) {
          setUser({ ...demoUser, email: email || demoUser.email });
          return;
        }
        if (!supabase) throw new Error("Renseignez les variables Supabase pour vous connecter.");
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        setUser(await profileFromUser(data.user));
        setIsAdmin(await adminStatusFromSession());
      },
      async signUp(email, password, displayName) {
        if (demoEnabled) throw new Error("L’inscription est désactivée dans la démonstration.");
        if (!supabase) throw new Error("Supabase n’est pas configuré.");
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: displayName } },
        });
        if (error) throw error;
        if (!data.session) return "confirmation-required";
        if (data.user) {
          setUser(await profileFromUser(data.user));
          setIsAdmin(await adminStatusFromSession());
        }
        return "signed-in";
      },
      async signOut() {
        if (!demoEnabled && supabase) {
          const { error } = await supabase.auth.signOut();
          if (error) throw error;
        }
        setUser(null);
        setIsAdmin(false);
        setRecoveryMode(false);
      },
      async sendPasswordReset(email) {
        if (demoEnabled) throw new Error("Le mot de passe de démonstration n’a pas besoin d’être réinitialisé.");
        if (!supabase) throw new Error("Supabase n’est pas configuré.");
        const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
          redirectTo: `${window.location.origin}/`,
        });
        if (error) throw error;
      },
      async updatePassword(password) {
        if (!supabase) throw new Error("Supabase n’est pas configuré.");
        const { error } = await supabase.auth.updateUser({ password });
        if (error) throw error;
        setRecoveryMode(false);
      },
      exitRecovery() {
        setRecoveryMode(false);
      },
      async updateProfile(displayName, avatarUrl) {
        if (!user) return;
        if (!demoEnabled && supabase) {
          const { error } = await supabase
            .from("profiles")
            .update({ display_name: displayName, avatar_url: avatarUrl ?? null })
            .eq("id", user.id);
          if (error) throw error;
        }
        setUser({ ...user, displayName, avatarUrl });
      },
    }),
    [isAdmin, loading, recoveryMode, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth doit être utilisé dans AuthProvider.");
  return value;
}
